import React, { FC } from 'react';
import {
  Body,
  Box,
  Flex,
  Glyph,
  Heading,
  HighlightedHeader,
  Modal,
  ModalContent,
  Spinner,
  useLockBodyScroll,
} from '@edreamsodigeo/prisma-design-system';
import { AlternativeItineraryDoubleCardLayerDesktopProps } from '@components/AlternativeItineraryDoubleCard/components/AlternativeItineraryDoubleCardLayer/types';
import FlightUnavailableBanner from '@components/AlternativeItineraryDoubleCard/components/FlightUnavailableBanner/FlightUnavailableBanner';
import { useTranslation, useTrackElement } from '@edreamsodigeo/frontend-core';
import AlternativeItineraryDoubleCardBenefits from '@components/AlternativeItineraryDoubleCard/components/AlternativeItineraryDoubleCardLayer/AlternativeItineraryDoubleCardBenefits';
import { useNumberOfPassengers } from '../../../../clientState/providers/SearchRequestProvider/index';
import AlternativeItineraryDoubleCardLayerButtons from '@components/AlternativeItineraryDoubleCard/components/AlternativeItineraryDoubleCardLayerButtons/AlternativeItineraryDoubleCardLayerButtons';
import AlternativeItineraryDoubleCardLayerCard from '../AlternativeItineraryDoubleCardLayerCard/AlternativeItineraryDoubleCardLayerCard';
import AlternativeItineraryDoubleCardLayerSectionTitle from '@components/AlternativeItineraryDoubleCard/components/AlternativeItineraryDoubleCardLayer/AlternativeDoubleCardLayerSectionTitle';
import { AlternativeItineraryDoubleCardShoppingCartLoading as ShoppingCartLoading } from './AlternativeItineraryDoubleCardShoppingCartLoading';
import {
  useAlternativeItinerary,
  useAlternativeItineraryActions,
} from 'clientState/providers/AlternativeItineraryProvider/AlternativeItineraryCalculatorProvider';

const LeftSideModal = ({
  itinerary,
  valueProposition,
  valuePropositions,
  discountAmount,
  selectedItinerary,
  additionalInformation,
  isBrokenFlow,
  nonIssuable,
  onContinue,
  onChange,
}) => {
  const { t } = useTranslation();
  const numPassengers = useNumberOfPassengers();
  return (
    <Flex
      flexDirection="column"
      justifyContent="center"
      backgroundColor="neutral.0"
      paddingRight="2x_big"
      width="50%"
      padding="2x_big"
    >
      {nonIssuable && (
        <Box>
          <FlightUnavailableBanner isBrokenFlow={isBrokenFlow} />
        </Box>
      )}

      <Box sx={{ marginTop: nonIssuable ? 'auto' : '32px' }}>
        {!nonIssuable &&
          (valueProposition === 'discount' || valueProposition === 'price') && (
            <Flex alignItems="flex-end" gap="1" sx={{ marginBottom: 'base' }}>
              <Box>
                <Glyph
                  category="Deals"
                  name="Hot"
                  size="small"
                  color="deals.40"
                />
              </Box>
              <Heading type="4" weight="medium" color="deals.40">
                {t('alternativeItinerary.layer.title.price')}
              </Heading>
            </Flex>
          )}

        <AlternativeItineraryDoubleCardLayerSectionTitle
          itinerary={itinerary}
          valueProposition={valueProposition}
          valuePropositions={valuePropositions}
          selectedItinerary={selectedItinerary}
          nonIssuable={nonIssuable}
          discountAmount={discountAmount}
        />
        {valuePropositions.length > 0 && (
          <AlternativeItineraryDoubleCardBenefits
            valuePropositions={valuePropositions}
            additionalInformation={additionalInformation}
            itinerary={itinerary}
            selectedItinerary={selectedItinerary}
            numPassengers={numPassengers}
          />
        )}
      </Box>
      <Box sx={{ marginTop: 'auto' }}>
        <AlternativeItineraryDoubleCardLayerButtons
          valueProposition={valueProposition}
          itinerary={itinerary}
          selectedItinerary={selectedItinerary}
          nonIssuable={nonIssuable}
          discountAmount={discountAmount}
          onContinue={onContinue}
          onChange={onChange}
        />
      </Box>
    </Flex>
  );
};

const RightSideModal = ({
  selectedItinerary,
  itinerary,
  valueProposition,
  isBrokenFlow,
  nonIssuable,
  trackingHandler,
}) => {
  const { t } = useTranslation();
  const pointerEvents = nonIssuable ? 'none' : 'auto';

  return (
    <Flex
      flexDirection="column"
      justifyContent="center"
      backgroundColor="neutral.20"
      padding="2x_big"
      width="50%"
    >
      <Box
        data-testid="alternative-selected-itinerary-desktop"
        marginBottom="base"
        sx={{ pointerEvents: pointerEvents }}
      >
        <AlternativeItineraryDoubleCardLayerCard
          itinerary={selectedItinerary}
          isBrokenFlow={isBrokenFlow}
          isUnavailable={nonIssuable}
          isClickable={false}
          isSelectedItinerary
          isAlternativeItineraryOnModal
          isAlternativeTopSection
          trackingHandler={trackingHandler}
        />
      </Box>
      <Box data-testid="alternative-itinerary-desktop">
        <HighlightedHeader
          type="positive"
          size="full"
          sx={{
            paddingBottom: '1x_small',
            paddingTop: '2x_small',
            marginBottom: 'minus_small',
            '::after': {
              content: 'none',
            },
          }}
        >
          <Body type="small" weight="normal" color="neutral.0">
            {t(
              `alternativeItinerary.ribbon.${
                valueProposition === 'discount' || valueProposition === 'price'
                  ? 'cheaper'
                  : 'expensive'
              }`
            )}
          </Body>
        </HighlightedHeader>
        <AlternativeItineraryDoubleCardLayerCard
          itinerary={itinerary}
          isClickable={true}
          isAlternativeItinerary
          hasAlternativeItineraryDecorator
          isAlternativeItineraryOnModal
          trackingHandler={trackingHandler}
        />
      </Box>
    </Flex>
  );
};

const AlternativeItineraryDoubleCardLayerDesktop: FC<
  AlternativeItineraryDoubleCardLayerDesktopProps
> = ({
  isOpen,
  loading,
  isShoppingCartLoading = false,
  itinerary,
  valueProposition,
  valuePropositions,
  discountAmount,
  selectedItinerary,
  additionalInformation,
  isBrokenFlow,
  nonIssuable,
  onContinue,
  onChange,
  onClose,
  trackingHandler,
}) => {
  const { altSearchId } = useAlternativeItinerary();
  const { addItineraryToUnavailableList } = useAlternativeItineraryActions();
  const displayTracking = useTrackElement(
    ['display'],
    `alternative-flights-layer-searchId-${altSearchId}-itineraryId-${selectedItinerary?.id}-alternativeItineraryId-${itinerary?.id}-${valueProposition}-discount:${discountAmount?.amount}${discountAmount?.currency}`
  );
  const trackingRef =
    altSearchId && itinerary && isOpen && !loading ? displayTracking : null;

  if (isOpen && !loading && nonIssuable) {
    addItineraryToUnavailableList(selectedItinerary);
  }

  useLockBodyScroll({ state: isOpen ? 'opened' : 'closed' });

  return (
    <div ref={trackingRef}>
      <Modal
        state={isOpen ? 'opened' : 'closed'}
        size="large"
        sx={{ width: '856px' }}
        onClose={!nonIssuable ? onClose : () => {}}
        data-testid="alternative-itinerary-card-layer-desktop"
      >
        <ModalContent sx={{ padding: 0 }}>
          {isShoppingCartLoading ? (
            <Flex height="461px" justifyContent="center" alignItems="center">
              <ShoppingCartLoading />
            </Flex>
          ) : loading || !itinerary ? (
            <Flex
              flexDirection="row"
              justifyContent="center"
              alignItems="center"
              width="100%"
              height="461px"
              data-testid="alternative-itinerary-card-layer-desktop-loading"
            >
              <Spinner size="xlarge" />
            </Flex>
          ) : (
            <Flex width="100%" height="461px">
              <LeftSideModal
                itinerary={itinerary}
                valueProposition={valueProposition}
                valuePropositions={valuePropositions}
                discountAmount={discountAmount}
                selectedItinerary={selectedItinerary}
                additionalInformation={additionalInformation}
                isBrokenFlow={isBrokenFlow}
                nonIssuable={nonIssuable}
                onContinue={onContinue}
                onChange={onChange}
              />
              <RightSideModal
                itinerary={itinerary}
                selectedItinerary={selectedItinerary}
                valueProposition={valueProposition}
                isBrokenFlow={isBrokenFlow}
                nonIssuable={nonIssuable}
                trackingHandler={trackingHandler}
              />
            </Flex>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
};

export default AlternativeItineraryDoubleCardLayerDesktop;
